import { apiRequests } from '../../http/config'
import { defaultIfEmpty, filter, mergeMap } from 'rxjs/operators'
import { EMPTY, forkJoin, Observable, of } from 'rxjs'
import { FileData } from '../../responses/files'
import { errorToast, pushErrorAndRethrow, toastFailedResponse } from '../../components/controls/Toaster/Toaster'
import { MultipleRowsResponse } from '../../responses/response'
import { RootEpic } from '../root'
import { createAction, isActionOf } from 'typesafe-actions'
import { ID } from '../../types/UtilityTypes'
import * as A from 'fp-ts/Array'
import * as IO from 'fp-ts/IO'
import * as IOOption from 'fp-ts-contrib/IOOption'

import { pipe } from 'fp-ts/function'
import { contramap, ordNumber } from 'fp-ts/Ord'
import { getItem } from 'fp-ts-local-storage'
import { Do } from 'fp-ts-contrib/Do'

const lengthOrd = contramap((file: File) => file.size)(ordNumber)
const EMPTY_RESPONSE: MultipleRowsResponse<FileData> = {
    success: true,
    rows: [],
    total: 0,
    version: '',
}

export const uploadFiles = (files: File[]): Observable<MultipleRowsResponse<FileData>> => {
    const program = pipe(
        Do(IOOption.ioOption)
        .bind('fileSize', pipe(
            getItem('max_file_size'),
            IOOption.filter(size => {
                const maxSize = parseInt(size)
                const bigFiles = files.filter(file => file.size > maxSize)

                if (bigFiles.length > 0) {
                    errorToast(
                        'Размер каждого из следующих файлов превышает ограничение:\n' +
                        files.map(file => file.name).join('\n')
                    )
                }
                return bigFiles.length === 0
            }),
        ))
        .bind('filesSize', getItem('max_files_size'))
        .bind('filesCount', getItem('max_files_count'))
        .return(({filesSize, filesCount}) => {
            const requests = pipe(
                files,
                A.sort(lengthOrd),
                A.reverse,
                A.chop((as) => {
                    let size = 0
                    let length = 0
                    const { init, rest } = A.spanLeft((file: File) => {
                        size += file.size
                        length++
                        return size < parseInt(filesSize) && (size === file.size || length < parseInt(filesCount))
                    })(as)
                    return [init, rest]
                }),
                A.filter(A.isNonEmpty),
                A.map(files => {
                    const data = new FormData()
                    for (const file of files) {
                        data.append('upload[]', file)
                    }
                    return apiRequests.file.upload({data}).pipe(
                        mergeMap(({ response }) => response.success ?
                            of(response) :
                            pushErrorAndRethrow(response, 'Не удалось загрузить файл')
                        ),
                    )
                }),
            )

            return forkJoin(requests).pipe(
                mergeMap((responses: MultipleRowsResponse<FileData>[]) => {
                    const rows = [...responses.flatMap(res => res.rows)]
                    const result: MultipleRowsResponse<FileData> = {
                        success: true,
                        rows,
                        total: rows.length,
                        version: '',
                    }
                    return of(result)
                }),
                defaultIfEmpty<MultipleRowsResponse<FileData>>(EMPTY_RESPONSE),
            )
        }),
        IOOption.getOrElse(() => IO.of(of(EMPTY_RESPONSE))),
    )
    return program()
}

export const filesActions = {
    downloadGroup: createAction('@FILES/DOWNLOAD_GROUP')<ID[]>()
}

const downloadGroup: RootEpic = action$ => action$.pipe(
    filter(isActionOf(filesActions.downloadGroup)),
    mergeMap(action => apiRequests.file.downloadGroup({ data: { files: action.payload } })),
    mergeMap(res => {
        if (res.response.success) {
            window.location.href = res.response.link
        } else {
            toastFailedResponse(res.response, 'Не удалось получить ссылку на скачивание')
        }
        return EMPTY
    }),
)

export const filesEpic = downloadGroup